import { useState } from 'react';

import { Alert, Dialog, DialogTitle } from '@mui/material';

import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import {
  createLeadStageRenderOption,
  createLeadStageRenderValue,
} from '@modules/_shared/utils/leadStageSelectHelpers';
import { useLocale } from '@shared/hooks/locale';

interface KabanMoveToColumnDialogProps {
  columns: { label: string; id: string; status?: string | null }[];
  onSubmit: (targetColumn: string) => Promise<any>;
  opened: boolean;
  onClose: () => void;
  title: React.ReactNode;
}

export const KabanMoveToColumnDialog: React.FC<
  KabanMoveToColumnDialogProps
> = ({ columns, opened, onClose, title, onSubmit }) => {
  const { t } = useLocale();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasStageStatus = columns.some(column => column.status != null);
  const formDefinition: FormDefinitionType<{ targetColumn: string }> = () => [
    {
      type: 'select',
      name: 'targetColumn',
      label: t('Destination column'),
      required: true,
      options: () =>
        columns.map(column => ({
          label: column.label,
          value: column.id,
        })),
      renderValue: hasStageStatus
        ? createLeadStageRenderValue(columns)
        : undefined,
      renderOption: hasStageStatus
        ? createLeadStageRenderOption(columns)
        : undefined,
      gridProps: { md: 12 },
    },
  ];

  return (
    <Dialog
      open={opened}
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
      }}
      slots={{
        transition: SlideUpTransition,
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <RaForm
        formDefinition={formDefinition}
        onCancel={onClose}
        onSubmit={async data => {
          if (data.targetColumn == null) {
            return;
          }

          try {
            await onSubmit(data.targetColumn);

            onClose();
          } catch (error) {
            if (error instanceof Error) {
              setErrorMessage(error.message);
            } else {
              setErrorMessage(
                t('An error occured while creating the buyer lead.'),
              );
            }
          }
        }}
      >
        {errorMessage != null && (
          <Alert severity="error" sx={{ m: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </RaForm>
    </Dialog>
  );
};
